import { Injectable, OnDestroy } from "@angular/core";
import { Subject } from "rxjs";

export interface SpeechTranscriptUpdate {
	/** Finalized text that should be appended to the committed comment. */
	final?: string;
	/** Current interim hypothesis; replaces any previous interim suffix. Empty when cleared. */
	interim?: string;
}

@Injectable({
	providedIn: "root",
})
export class SpeechRecognitionService implements OnDestroy {
	private recognition: any = null;
	private intentionallyStopped = false;
	private restarting = false;
	private transcriptSubject = new Subject<SpeechTranscriptUpdate>();
	private errorSubject = new Subject<string>();
	private endSubject = new Subject<void>();

	readonly transcript$ = this.transcriptSubject.asObservable();
	readonly error$ = this.errorSubject.asObservable();
	readonly end$ = this.endSubject.asObservable();

	isSupported(): boolean {
		return !!this.getSpeechRecognitionCtor();
	}

	isListening(): boolean {
		return !!this.recognition || this.restarting;
	}

	startListening(): void {
		if (!this.isSupported()) {
			this.errorSubject.next("Speech recognition is not supported in this browser.");
			return;
		}

		this.intentionallyStopped = false;
		this.beginRecognitionSession();
	}

	stopListening(): void {
		this.intentionallyStopped = true;
		this.restarting = false;
		this.teardownRecognition();
	}

	appendTranscript(currentValue: string, transcript: string): string {
		const base = String(currentValue ?? "");
		if (!transcript) {
			return base;
		}

		if (!base.trim()) {
			return transcript;
		}

		const needsSpace = !base.endsWith(" ") && !base.endsWith("\n");
		return `${base}${needsSpace ? " " : ""}${transcript}`;
	}

	ngOnDestroy(): void {
		this.stopListening();
		this.transcriptSubject.complete();
		this.errorSubject.complete();
		this.endSubject.complete();
	}

	private beginRecognitionSession(): void {
		this.teardownRecognition();

		const Recognition = this.getSpeechRecognitionCtor();
		if (!Recognition) {
			return;
		}

		const recognition = new Recognition();
		recognition.continuous = true;
		recognition.interimResults = true;
		recognition.lang = "en-US";

		recognition.onresult = (event: any) => {
			let finalTranscript = "";
			let interimTranscript = "";
			const results = event.results as ArrayLike<{ isFinal: boolean; 0?: { transcript?: string } }>;
			const resultIndex = typeof event.resultIndex === "number" ? event.resultIndex : 0;

			for (let i = resultIndex; i < results.length; i++) {
				const result = results[i];
				const text = result[0]?.transcript ?? "";
				if (result.isFinal) {
					finalTranscript += text;
				} else {
					interimTranscript += text;
				}
			}

			const update: SpeechTranscriptUpdate = {};
			if (finalTranscript.trim()) {
				update.final = finalTranscript.trim();
			}
			update.interim = interimTranscript.trim();

			if (update.final || update.interim !== undefined) {
				this.transcriptSubject.next(update);
			}
		};

		recognition.onerror = (event: any) => {
			const message = event?.error as string | undefined;
			if (!message || message === "aborted" || message === "no-speech") {
				return;
			}

			if (message === "not-allowed" || message === "service-not-allowed") {
				this.errorSubject.next("Microphone permission is required for dictation.");
				this.intentionallyStopped = true;
				return;
			}

			if (message === "network") {
				this.errorSubject.next("Speech recognition network error. Please try again.");
				return;
			}

			this.errorSubject.next(`Speech recognition error: ${message}`);
		};

		recognition.onend = () => {
			this.recognition = null;

			if (this.intentionallyStopped) {
				this.restarting = false;
				this.endSubject.next();
				return;
			}

			// Safari/WebKit often ends continuous sessions early; restart while still desired.
			// Defer restart so the browser can fully release the previous session.
			this.restarting = true;
			setTimeout(() => {
				if (this.intentionallyStopped) {
					this.restarting = false;
					this.endSubject.next();
					return;
				}

				try {
					this.beginRecognitionSession();
				} catch {
					this.restarting = false;
					this.endSubject.next();
					this.errorSubject.next("Unable to continue speech recognition.");
				}
			}, 0);
		};

		this.recognition = recognition;
		this.restarting = false;

		try {
			recognition.start();
		} catch {
			this.recognition = null;
			if (!this.intentionallyStopped) {
				this.errorSubject.next("Unable to start speech recognition.");
				this.endSubject.next();
			}
		}
	}

	private teardownRecognition(): void {
		if (!this.recognition) {
			return;
		}

		const recognition = this.recognition;
		this.recognition = null;

		try {
			recognition.onresult = null;
			recognition.onerror = null;
			recognition.onend = null;
			recognition.stop();
		} catch {
			// ignore stop errors when recognition already ended
		}
	}

	private getSpeechRecognitionCtor(): (new () => any) | null {
		if (typeof window === "undefined") {
			return null;
		}

		const win = window as Window & {
			SpeechRecognition?: new () => any;
			webkitSpeechRecognition?: new () => any;
		};

		return win.SpeechRecognition ?? win.webkitSpeechRecognition ?? null;
	}
}
